import React, { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConfirm } from "../../../context/ConfirmContext";
import { updateHouseholdAPI, getBeneficiaryShareTotalErrorsFromForm, normalizeCountryOfCitizenshipInPersonalInfo} from "../../../api/updateHouseholdAPI";
import { formatDateForInput, formatAccountContextLabel, isTwoOwnerAccount } from "../../../utils/formatters";
import {
  normalizeToYmd,
  validateDobYmd,
  validatePastOnlyYmd,
  validateScheduleFutureYmd,
} from "../../../utils/dateFieldConstraints";
import {
  trusteeEntryToObject,
  isRevocableTrust,
  getTrusteeMemberId,
  finalizeTrusteesForPayload,
  isPlaceholderTrusteeRelationship,
  buildTrustInfoUpdatePayload,
  getTrustGrantorIds,
} from "../../../utils/trustInfoHelpers";
import {
  shouldDisableEmploymentDetails,
  normalizeEmploymentInfoForStatus,
} from "../../../constants/membersConstants";
import { getTaxClassificationOptions } from "../../../constants/accountConstants";
import ClientPortalLayout from "../../ClientPortalLayout";
import TabBar from "../shared/TabBar";
import ReviewHouseholdInfoTab from "./ReviewHouseholdInfoTab";
import ReviewMembersTab from "./ReviewMembersTab";
import ReviewAccountsTab from "./ReviewAccountsTab";
import ReviewBankAccountsTab from "./ReviewBankAccountsTab";
import SubmitPanel from "./SubmitPanel";
import AddMemberBeneficiaryTrustedModal from "../../AddMemberBeneficiaryTrustedModal";
import AddMemberModal from "../../AddMemberModal";
import SelectMemberTypeModal from "../../SelectMemberTypeModal";
import AddContributionModal from "../../AddContributionModal";
import AddWithdrawalModal from "../../AddWithdrawalModal";
import SelectBankAccountModal from "../../SelectBankAccountModal";
import AddBankAccountModal from "../../AddBankAccountModal";
import clientAxiosInstance from "../../../api/clientAxiosInstance";
import {
  buildClientBankAccountsOp,
  emptyBankOps,
  findHouseholdAccountsLinkedToBank,
  stageFieldEdit,
  stageLinkExisting,
  stageUnlink,
} from "../../../utils/bankAccounts";

const ReviewAndEditV2 = ({
  householdData,
  household_id,
  clientToken,
  onLogout,
  onDataUpdate,
}) => {
  const navigate = useNavigate();
  const confirmDialog = useConfirm();
  const { household_id: householdIdFromRoute } = useParams();
  const actualHouseholdId = household_id || householdIdFromRoute;

  const [activeTab, setActiveTab] = useState("household");

  // Per-tab edit state
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMembers, setIsEditingMembers] = useState(false);
  const [isEditingAccounts, setIsEditingAccounts] = useState(false);
  const [isEditingBankAccounts, setIsEditingBankAccounts] = useState(false);

  // Per-tab form data
  const [householdFormData, setHouseholdFormData] = useState({});
  const [membersFormData, setMembersFormData] = useState({});
  const [accountsFormData, setAccountsFormData] = useState({});
  const [bankAccountsFormData, setBankAccountsFormData] = useState({});

  // Per-tab saving state
  const [saving, setSaving] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const [savingAccounts, setSavingAccounts] = useState(false);
  const [savingBankAccounts, setSavingBankAccounts] = useState(false);

  // Shared state
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [clearingAdvisorNote, setClearingAdvisorNote] = useState(false);

  const handleClearAdvisorNote = useCallback(async () => {
    if (!actualHouseholdId || !clientToken || clearingAdvisorNote) return;
    setClearingAdvisorNote(true);
    try {
      await clientAxiosInstance.delete(
        `/client/household/${actualHouseholdId}/advisor-note`,
        { headers: { Authorization: `Bearer ${clientToken}` } },
      );
      if (onDataUpdate) onDataUpdate();
    } catch (e) {
      console.error("Failed to clear advisor note:", e);
    } finally {
      setClearingAdvisorNote(false);
    }
  }, [actualHouseholdId, clientToken, clearingAdvisorNote, onDataUpdate]);

  const confirmClearAdvisorNote = useCallback(async () => {
    if (!householdData?.advisor_note || householdData.advisor_note.trim() === "")
      return;
    const ok = await confirmDialog({
      title: "Delete advisor note?",
      message: "This note will be removed and cannot be shown again.",
      confirmLabel: "Delete",
    });
    if (ok) handleClearAdvisorNote();
  }, [householdData?.advisor_note, handleClearAdvisorNote, confirmDialog]);

  // Modal state for Add Beneficiary / Trusted Contact
  const [showSelectMemberTypeModal, setShowSelectMemberTypeModal] = useState(false);
  const [memberTypeModalType, setMemberTypeModalType] = useState(null);
  const [accountForBeneficiary, setAccountForBeneficiary] = useState(null);
  const [showBeneficiaryTrustedModal, setShowBeneficiaryTrustedModal] = useState(false);
  const [newlyCreatedMemberId, setNewlyCreatedMemberId] = useState(null);

  // Modal state for Add Contribution / Withdrawal
  const [showAddContributionModal, setShowAddContributionModal] = useState(false);
  const [showAddWithdrawalModal, setShowAddWithdrawalModal] = useState(false);
  const [accountForMoneyMovement, setAccountForMoneyMovement] = useState(null);

  // Modal state for Bank Account linking
  const [showSelectBankAccountModal, setShowSelectBankAccountModal] = useState(false);
  const [accountForBankLinking, setAccountForBankLinking] = useState(null);
  const [showAddBankAccountModal, setShowAddBankAccountModal] = useState(false);

  // Modal state for Add New Member (standalone vs. after "Add new" in beneficiary/trusted flow)
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const addMemberForBeneficiaryFlowRef = useRef(false);

  // Removed beneficiaries/trusted contacts (keyed by accountId → array of IDs)
  const [removedBeneficiaries, setRemovedBeneficiaries] = useState({});
  const [removedTrustedContacts, setRemovedTrustedContacts] = useState({});

  const hasUnsavedEdits = isEditing || isEditingMembers || isEditingAccounts || isEditingBankAccounts;

  const household = householdData || {};

  const selectMemberModalExcludeMemberIds = useMemo(() => {
    if (!accountForBeneficiary) return [];
    const acct = (household.accounts || []).find((a) => a.id === accountForBeneficiary);
    if (!acct) return [];
    if (memberTypeModalType === "beneficiary") {
      const removed = new Set(removedBeneficiaries[accountForBeneficiary] || []);
      const ids = new Set();
      (acct.beneficiaries || []).forEach((b, originalIdx) => {
        if (removed.has(b.id)) return;
        const formMember =
          accountsFormData[accountForBeneficiary]?.beneficiaries?.[originalIdx]
            ?.household_member_id;
        const memberId =
          formMember !== undefined &&
          formMember !== null &&
          formMember !== ""
            ? formMember
            : b.household_member_id;
        if (memberId) ids.add(memberId);
      });
      return [...ids];
    }
    if (memberTypeModalType === "trusted_contact") {
      const removed = new Set(removedTrustedContacts[accountForBeneficiary] || []);
      const ids = new Set();
      (acct.trusted_contacts || []).forEach((tc, originalIdx) => {
        if (removed.has(tc.id)) return;
        const formMember =
          accountsFormData[accountForBeneficiary]?.trusted_contacts?.[originalIdx]
            ?.household_member_id;
        const memberId =
          formMember !== undefined &&
          formMember !== null &&
          formMember !== ""
            ? formMember
            : tc.household_member_id;
        if (memberId) ids.add(memberId);
      });
      return [...ids];
    }
    return [];
  }, [
    accountForBeneficiary,
    memberTypeModalType,
    household.accounts,
    removedBeneficiaries,
    removedTrustedContacts,
    accountsFormData,
  ]);
  const memberCount = household.members?.length || 0;
  const accountCount = household.accounts?.length || 0;
  const bankAccountCount = household.bank_accounts?.length || 0;

  const getMemberSsnDigitsById = useCallback(
    (memberId) => {
      if (!memberId) return "";
      const member = (household.members || []).find((m) => m.id === memberId);
      return String(member?.personal_info?.ssn_or_tin || "").replace(/\D/g, "");
    },
    [household.members],
  );

  const handleFieldError = useCallback((fieldPath, errorMessage) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (errorMessage) {
        newErrors[fieldPath] = errorMessage;
      } else {
        delete newErrors[fieldPath];
      }
      return newErrors;
    });
  }, []);

  // ── Add Beneficiary / Trusted Contact handlers ──
  const handleAddBeneficiaryForAccount = useCallback((accountId) => {
    setAccountForBeneficiary(accountId);
    setMemberTypeModalType("beneficiary");
    setShowSelectMemberTypeModal(true);
  }, []);

  const handleAddTrustedContactForAccount = useCallback((accountId) => {
    setAccountForBeneficiary(accountId);
    setMemberTypeModalType("trusted_contact");
    setShowSelectMemberTypeModal(true);
  }, []);

  const handleSelectExistingMember = useCallback((member) => {
    setShowSelectMemberTypeModal(false);
    setNewlyCreatedMemberId(member.id);
    setShowBeneficiaryTrustedModal(true);
  }, []);

  // ── Remove Beneficiary / Trusted Contact handlers ──
  const handleRemoveBeneficiary = useCallback(async (accountId, beneficiaryId) => {
    const ok = await confirmDialog({
      title: "Remove beneficiary?",
      message: "The change will apply when you save.",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setRemovedBeneficiaries((prev) => ({
      ...prev,
      [accountId]: [...(prev[accountId] || []), beneficiaryId],
    }));
  }, [confirmDialog]);

  const handleRemoveTrustedContact = useCallback(async (accountId, trustedContactId) => {
    const ok = await confirmDialog({
      title: "Remove trusted contact?",
      message: "The change will apply when you save.",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setRemovedTrustedContacts((prev) => ({
      ...prev,
      [accountId]: [...(prev[accountId] || []), trustedContactId],
    }));
  }, [confirmDialog]);

  // ── Add Contribution / Withdrawal handlers ──
  const handleAddContribution = useCallback((account) => {
    setAccountForMoneyMovement(account);
    setShowAddContributionModal(true);
  }, []);

  const handleAddWithdrawal = useCallback((account) => {
    setAccountForMoneyMovement(account);
    setShowAddWithdrawalModal(true);
  }, []);

  const handleDeleteDeposit = useCallback(async (accountId, depositId) => {
    const ok = await confirmDialog({ title: "Delete contribution?", confirmLabel: "Delete" });
    if (!ok) return;
    const result = await updateHouseholdAPI(actualHouseholdId, clientToken, {
      accounts: { [accountId]: { deposits: { delete: [depositId] } } },
    });
    if (result.success) {
      setSuccess("Contribution deleted.");
      if (onDataUpdate && result.data) onDataUpdate(result.data);
      setTimeout(() => setSuccess(""), 3000);
    } else {
      setError(result.error || "Failed to delete contribution.");
    }
  }, [actualHouseholdId, clientToken, onDataUpdate]);

  const handleDeleteWithdrawal = useCallback(async (accountId, withdrawalId) => {
    const ok = await confirmDialog({ title: "Delete withdrawal?", confirmLabel: "Delete" });
    if (!ok) return;
    const result = await updateHouseholdAPI(actualHouseholdId, clientToken, {
      accounts: { [accountId]: { withdrawals: { delete: [withdrawalId] } } },
    });
    if (result.success) {
      setSuccess("Withdrawal deleted.");
      if (onDataUpdate && result.data) onDataUpdate(result.data);
      setTimeout(() => setSuccess(""), 3000);
    } else {
      setError(result.error || "Failed to delete withdrawal.");
    }
  }, [actualHouseholdId, clientToken, onDataUpdate]);

  // ── Household field change ──
  const handleHouseholdFieldChange = useCallback((fieldPath, value) => {
    setHouseholdFormData((prev) => {
      const updated = { ...prev };
      const keys = fieldPath.split(".");
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  }, []);

  const getHouseholdFormValue = useCallback(
    (path, fallback) => {
      const keys = path.split(".");
      let current = householdFormData;
      for (const key of keys) {
        if (current && current[key] !== undefined) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    },
    [householdFormData],
  );

  // ── Members field change ──
  const handleMemberFieldChange = useCallback((memberId, fieldPath, value) => {
    setMembersFormData((prev) => {
      const updated = { ...prev };
      if (!updated[memberId]) updated[memberId] = {};
      const keys = fieldPath.split(".");
      let current = updated[memberId];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      if (
        fieldPath === "employment_info.status" &&
        shouldDisableEmploymentDetails(value)
      ) {
        current.occupation = null;
        current.industry = null;
        current.employer = null;
        current.address = null;
        current.title = null;
        current.tin = null;
      }
      return updated;
    });
  }, []);

  // ── Accounts field change ──
  const handleAccountFieldChange = useCallback((accountId, fieldPath, value) => {
    setAccountsFormData((prev) => {
      const updated = { ...prev };
      if (!updated[accountId]) updated[accountId] = {};
      const keys = fieldPath.split(".");
      let current = updated[accountId];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  }, []);

  // ── Bank Account link/unlink handlers ──
  const mutateBankOps = useCallback((accountId, mutator) => {
    setAccountsFormData((prev) => {
      const updated = { ...prev };
      const accountEntry = { ...(updated[accountId] || {}) };
      const currentOps = accountEntry.bankAccountsOps || emptyBankOps();
      accountEntry.bankAccountsOps = mutator(currentOps);
      updated[accountId] = accountEntry;
      return updated;
    });
  }, []);

  const handleLinkBankAccount = useCallback((accountId) => {
    setAccountForBankLinking(accountId);
    setShowSelectBankAccountModal(true);
  }, []);

  const handleRemoveBankAccount = useCallback(async (accountId, bankAccountId) => {
    if (!bankAccountId) return;
    const ok = await confirmDialog({
      title: "Unlink bank account?",
      message: "Changes will apply when you save.",
      confirmLabel: "Unlink",
    });
    if (!ok) return;
    mutateBankOps(accountId, (ops) => stageUnlink(ops, bankAccountId));
  }, [mutateBankOps, confirmDialog]);

  const handleSelectExistingBankAccount = useCallback((bankAccountId) => {
    setShowSelectBankAccountModal(false);
    if (accountForBankLinking) {
      mutateBankOps(accountForBankLinking, (ops) => stageLinkExisting(ops, bankAccountId));
      setAccountForBankLinking(null);
    }
  }, [accountForBankLinking, mutateBankOps]);

  const handleEditLinkedBankAccountField = useCallback((accountId, bankAccountId, fieldPath, value) => {
    mutateBankOps(accountId, (ops) => stageFieldEdit(ops, bankAccountId, fieldPath, value));
  }, [mutateBankOps]);

  // ── Bank Accounts field change ──
  const handleBankAccountFieldChange = useCallback((bankAccountId, fieldPath, value) => {
    setBankAccountsFormData((prev) => {
      const updated = { ...prev };
      if (!updated[bankAccountId]) updated[bankAccountId] = {};
      const keys = fieldPath.split(".");
      let current = updated[bankAccountId];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  }, []);

  // ── Save handlers ──

  const buildPersonalInfoUpdate = (member, personalUpdates) => {
    const basePersonal = member.personal_info || {};
    const updatedPersonal = { ...basePersonal };

    if (personalUpdates) {
      Object.keys(personalUpdates).forEach((key) => {
        if (key !== "address") {
          updatedPersonal[key] = personalUpdates[key] !== undefined ? personalUpdates[key] : basePersonal[key];
        }
      });
    }

    if (personalUpdates?.address) {
      const existingAddresses = Array.isArray(basePersonal.address) ? [...basePersonal.address] : [];
      const updatedAddresses = [...existingAddresses];
      Object.keys(personalUpdates.address).forEach((typeKey) => {
        const addressUpdates = personalUpdates.address[typeKey] || {};
        const existingIndex = updatedAddresses.findIndex((addr) => addr?.type === typeKey);
        const baseAddress = existingIndex >= 0 ? { ...updatedAddresses[existingIndex] } : { type: typeKey };
        const mergedAddress = { ...baseAddress, ...addressUpdates };
        if (existingIndex >= 0) {
          updatedAddresses[existingIndex] = mergedAddress;
        } else {
          updatedAddresses.push(mergedAddress);
        }
      });
      updatedPersonal.address = updatedAddresses;
    } else if (basePersonal.address) {
      updatedPersonal.address = Array.isArray(basePersonal.address) ? [...basePersonal.address] : basePersonal.address;
    }

    if (updatedPersonal.dob) {
      updatedPersonal.dob = formatDateForInput(updatedPersonal.dob);
    }

    if (personalUpdates?.ssn_or_tin) {
      updatedPersonal.ssn_or_tin = personalUpdates.ssn_or_tin.replace(/\D/g, "");
    }

    if (updatedPersonal.phone) {
      updatedPersonal.phone = String(updatedPersonal.phone).replace(/\D/g, "");
    }

    if (Array.isArray(updatedPersonal.address)) {
      updatedPersonal.address = updatedPersonal.address.map((addr) => {
        if (addr?.phone) return { ...addr, phone: String(addr.phone).replace(/\D/g, "") };
        return addr;
      });
    }

    return normalizeCountryOfCitizenshipInPersonalInfo(updatedPersonal);
  };

  const buildEmploymentInfoUpdate = (member, employmentUpdates) => {
    const baseEmployment = member.employment_info || {};
    const updatedEmployment = { ...baseEmployment };
    if (employmentUpdates) {
      Object.keys(employmentUpdates).forEach((key) => {
        if (key !== "address") {
          updatedEmployment[key] = employmentUpdates[key];
        }
      });
    }
    if (
      employmentUpdates &&
      Object.prototype.hasOwnProperty.call(employmentUpdates, "address")
    ) {
      if (employmentUpdates.address == null) {
        updatedEmployment.address = null;
      } else {
        const baseAddress = baseEmployment.address || {};
        updatedEmployment.address = { ...baseAddress, ...employmentUpdates.address };
      }
    }
    return normalizeEmploymentInfoForStatus(updatedEmployment);
  };

  const buildIdInfoUpdate = (member, idUpdates) => {
    const baseIds = Array.isArray(member.id_info) ? member.id_info : [];
    const updatedIds = [...baseIds];
    if (idUpdates) {
      Object.keys(idUpdates).forEach((indexKey) => {
        const index = parseInt(indexKey, 10);
        if (Number.isNaN(index)) return;
        while (updatedIds.length <= index) updatedIds.push({});
        const fields = { ...idUpdates[indexKey] };
        if (fields.issuance_date) fields.issuance_date = formatDateForInput(fields.issuance_date);
        if (fields.expiration_date) fields.expiration_date = formatDateForInput(fields.expiration_date);
        updatedIds[index] = { ...(updatedIds[index] || {}), ...fields };
      });
    }
    return updatedIds;
  };

  const handleSaveHousehold = async () => {
    if (!actualHouseholdId || !clientToken) {
      setError("Missing required information.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const householdPayload = {};

      if (householdFormData.annual_income !== undefined) {
        householdPayload.annual_income = householdFormData.annual_income ? parseFloat(householdFormData.annual_income) : null;
      }
      if (householdFormData.estimated_liquid_net_worth !== undefined) {
        householdPayload.estimated_liquid_net_worth = householdFormData.estimated_liquid_net_worth ? parseFloat(householdFormData.estimated_liquid_net_worth) : null;
      }
      if (householdFormData.estimated_total_net_worth !== undefined) {
        householdPayload.estimated_total_net_worth = householdFormData.estimated_total_net_worth ? parseFloat(householdFormData.estimated_total_net_worth) : null;
      }
      if (householdFormData.aum !== undefined) {
        householdPayload.aum = householdFormData.aum ? parseFloat(householdFormData.aum) : null;
      }

      if (householdFormData.tax_info?.tax_bracket) {
        const orig = household.tax_info?.tax_bracket || {};
        householdPayload.tax_info = {
          tax_bracket: {
            start: householdFormData.tax_info.tax_bracket.start !== undefined ? (householdFormData.tax_info.tax_bracket.start ? parseFloat(householdFormData.tax_info.tax_bracket.start) : null) : (orig.start ?? null),
            end: householdFormData.tax_info.tax_bracket.end !== undefined ? (householdFormData.tax_info.tax_bracket.end ? parseFloat(householdFormData.tax_info.tax_bracket.end) : null) : (orig.end ?? null),
          },
        };
      }

      ["annual_expense_range", "special_expense_range"].forEach((rangeKey) => {
        if (householdFormData[rangeKey]) {
          const orig = household[rangeKey] || {};
          householdPayload[rangeKey] = {
            start: householdFormData[rangeKey].start !== undefined ? (householdFormData[rangeKey].start ? parseFloat(householdFormData[rangeKey].start) : null) : (orig.start ?? null),
            end: householdFormData[rangeKey].end !== undefined ? (householdFormData[rangeKey].end ? parseFloat(householdFormData[rangeKey].end) : null) : (orig.end ?? null),
          };
        }
      });

      if (householdFormData.investor_associations) {
        const base = {
          accredited_investor: household.investor_associations?.accredited_investor ?? false,
          us_broker_dealer_association: household.investor_associations?.us_broker_dealer_association ?? false,
          broker_dealer_employee: household.investor_associations?.broker_dealer_employee ?? false,
          ria_association: household.investor_associations?.ria_association ?? false,
          shareholder_or_member_board_director: household.investor_associations?.shareholder_or_member_board_director ?? false,
        };
        householdPayload.investor_associations = { ...base, ...householdFormData.investor_associations };
      }

      if (householdFormData.investor_experience) {
        const base = {
          bonds: household.investor_experience?.bonds ?? null,
          stocks: household.investor_experience?.stocks ?? null,
          alternative_investments: household.investor_experience?.alternative_investments ?? null,
          variable_annuities: household.investor_experience?.variable_annuities ?? null,
          mutual_funds: household.investor_experience?.mutual_funds ?? null,
          options: household.investor_experience?.options ?? null,
          partnerships: household.investor_experience?.partnerships ?? null,
        };
        const updated = { ...base };
        Object.keys(householdFormData.investor_experience).forEach((key) => {
          const raw = householdFormData.investor_experience[key];
          updated[key] = raw !== undefined && raw !== null && raw !== "" ? parseInt(raw, 10) : base[key];
        });
        householdPayload.investor_experience = updated;
      }

      if (Object.keys(householdPayload).length === 0) {
        setIsEditing(false);
        setHouseholdFormData({});
        setSaving(false);
        return;
      }

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, { household: householdPayload });

      if (result.success) {
        setSuccess(result.message || "Household information updated successfully!");
        setIsEditing(false);
        setHouseholdFormData({});
        if (onDataUpdate && result.data) onDataUpdate(result.data);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(result.error || "Failed to update.");
        if (result.sessionExpired) setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelHousehold = () => {
    setIsEditing(false);
    setHouseholdFormData({});
    setValidationErrors({});
  };

  const handleSaveMembers = async () => {
    if (!actualHouseholdId || !clientToken) {
      setError("Missing required information.");
      return;
    }
    for (const memberId of Object.keys(membersFormData)) {
      const member = household.members?.find((m) => m.id === memberId);
      if (!member) continue;
      const changes = membersFormData[memberId] || {};
      if (!changes.personal_info) continue;
      const mergedPersonal = buildPersonalInfoUpdate(member, changes.personal_info);
      if (mergedPersonal.dob) {
        const dobMsg = validateDobYmd(normalizeToYmd(mergedPersonal.dob), member.is_primary === true);
        if (dobMsg) {
          setError(dobMsg);
          return;
        }
      }
    }

    setSavingMembers(true);
    setError("");
    setSuccess("");

    try {
      const householdMembersPayload = {};
      Object.keys(membersFormData).forEach((memberId) => {
        const member = household.members?.find((m) => m.id === memberId);
        if (!member) return;
        const changes = membersFormData[memberId] || {};
        const memberUpdate = {};
        if (changes.personal_info) memberUpdate.personal_info = buildPersonalInfoUpdate(member, changes.personal_info);
        if (changes.employment_info) memberUpdate.employment_info = buildEmploymentInfoUpdate(member, changes.employment_info);
        if (changes.id_info) memberUpdate.id_info = buildIdInfoUpdate(member, changes.id_info);
        if (Object.keys(memberUpdate).length > 0) householdMembersPayload[memberId] = memberUpdate;
      });

      if (Object.keys(householdMembersPayload).length === 0) {
        setIsEditingMembers(false);
        setMembersFormData({});
        setSavingMembers(false);
        return;
      }

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, { household_members: householdMembersPayload });
      if (result.success) {
        setSuccess(result.message || "Members updated successfully!");
        setIsEditingMembers(false);
        setMembersFormData({});
        setValidationErrors({});
        if (onDataUpdate && result.data) onDataUpdate(result.data);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(result.error || "Failed to update members.");
        if (result.sessionExpired) setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSavingMembers(false);
    }
  };

  const handleCancelMembers = () => {
    setIsEditingMembers(false);
    setMembersFormData({});
    setValidationErrors({});
  };

  const handleSaveAccounts = async () => {
    if (!actualHouseholdId || !clientToken) {
      setError("Missing required information.");
      return;
    }

    const getAccFormValue = (path, fallback) => {
      const keys = path.split(".");
      let current = accountsFormData;
      for (const key of keys) {
        if (current && current[key] !== undefined) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    };

    const accountsMissingOwner1 = [];
    const twoOwnerAccountsMissingOwner2 = [];
    (household.accounts || []).forEach((account) => {
      const ownerId1 = getAccFormValue(`${account.id}.owners_info.0.id`, account.owners_info?.[0]?.id);
      const isNonProfitBusinessAccount =
        (account.registration || "").toLowerCase() === "business" &&
        getAccFormValue(`${account.id}.business_info.is_non_profit`, account.business_info?.is_non_profit) === true;
      if (!ownerId1 && !isNonProfitBusinessAccount) accountsMissingOwner1.push(account);
      if (isTwoOwnerAccount(account)) {
        const ownerId2 = getAccFormValue(`${account.id}.owners_info.1.id`, account.owners_info?.[1]?.id);
        if (!ownerId2) twoOwnerAccountsMissingOwner2.push(account);
      }
    });
    if (accountsMissingOwner1.length > 0) {
      setError("Every account must have an Owner 1 assigned.");
      return;
    }
    if (twoOwnerAccountsMissingOwner2.length > 0) {
      setError("Accounts requiring two owners must have both Owner 1 and Owner 2 assigned.");
      return;
    }

    const beneficiaryErrors = [];
    const accountsList = household.accounts || [];
    const membersList = household.members || [];
    const hhName = household.name || "";
    accountsList.forEach((account) => {
      const form = accountsFormData[account.id] || {};
      const benForm = form.beneficiaries || {};
      const removed = removedBeneficiaries[account.id] || [];
      const accLabel = formatAccountContextLabel(account, accountsList, membersList, hhName);
      getBeneficiaryShareTotalErrorsFromForm(account, benForm, removed).forEach((msg) => {
        beneficiaryErrors.push(`${accLabel}: ${msg}`);
      });
    });
    if (beneficiaryErrors.length > 0) {
      setError(beneficiaryErrors.join(" · "));
      return;
    }

    const getDuplicateTrusteeIds = (ids) => {
      const seen = new Set();
      const dups = new Set();
      (ids || []).forEach((raw) => {
        if (raw === null || raw === undefined || raw === "") return;
        const id = String(raw);
        if (seen.has(id)) dups.add(id);
        else seen.add(id);
      });
      return Array.from(dups);
    };

    for (const account of accountsList) {
      if (!account.trust_info || Object.keys(account.trust_info).length === 0) {
        continue;
      }
      const accountId = account.id;
      const changes = accountsFormData[accountId] || {};
      const baseT = Array.isArray(account.trust_info.trustees)
        ? account.trust_info.trustees
        : [];
      const chT = changes.trust_info?.trustees || {};
      const grantorIdsRaw = getAccFormValue(
        `${accountId}.trust_info.grantorIds`,
        getTrustGrantorIds(account.trust_info),
      );
      const grantorIds = Array.isArray(grantorIdsRaw) ? grantorIdsRaw.filter(Boolean) : [];
      const grantorIdSetValidation = new Set(grantorIds.map(String));
      const idxKeys = Object.keys(chT)
        .map(Number)
        .filter(Number.isFinite);
      const maxFromChanges = idxKeys.length ? Math.max(...idxKeys) : -1;
      const maxIdx = Math.max(baseT.length - 1, maxFromChanges, -1);
      const resolvedIds = [];
      for (let i = 0; i <= maxIdx; i++) {
        const tid =
          chT[i]?.id !== undefined
            ? chT[i].id
            : baseT[i]
              ? getTrusteeMemberId(baseT[i])
              : "";
        if (tid) resolvedIds.push(String(tid));
      }
      const trusteeDups = getDuplicateTrusteeIds(resolvedIds);
      if (trusteeDups.length > 0) {
        const accLabel = formatAccountContextLabel(
          account,
          accountsList,
          membersList,
          hhName,
        );
        setError(
          `${accLabel}: Duplicate trustees — each household member can only be listed once as a trustee.`,
        );
        return;
      }
      for (let i = 0; i <= maxIdx; i++) {
        const tid =
          chT[i]?.id !== undefined
            ? chT[i].id
            : baseT[i]
              ? getTrusteeMemberId(baseT[i])
              : "";
        if (!tid) continue;
        if (grantorIdSetValidation.has(String(tid))) continue;
        const rel =
          chT[i]?.relationship_to_grantor !== undefined
            ? chT[i].relationship_to_grantor
            : baseT[i]?.relationship_to_grantor;
        if (isPlaceholderTrusteeRelationship(rel)) {
          const accLabel = formatAccountContextLabel(
            account,
            accountsList,
            membersList,
            hhName,
          );
          setError(
            `${accLabel}: Choose Relationship to Grantor for each trustee who is not the grantor.`,
          );
          return;
        }
      }
    }

    const allAccountIdsForDates = new Set([
      ...Object.keys(accountsFormData),
      ...Object.keys(removedBeneficiaries),
      ...Object.keys(removedTrustedContacts),
    ]);
    for (const accountId of allAccountIdsForDates) {
      const account = accountsList.find((a) => a.id === accountId);
      if (!account) continue;
      const accountChanges = accountsFormData[accountId] || {};
      const accLabel = formatAccountContextLabel(account, accountsList, membersList, hhName);
      const mergedTrust = { ...(account.trust_info || {}), ...(accountChanges.trust_info || {}) };
      if (mergedTrust.execution_date) {
        const tMsg = validatePastOnlyYmd(normalizeToYmd(mergedTrust.execution_date), "Execution date");
        if (tMsg) {
          setError(`${accLabel}: ${tMsg}`);
          return;
        }
      }
      if (mergedTrust.amended_date) {
        const aMsg = validatePastOnlyYmd(normalizeToYmd(mergedTrust.amended_date), "Amended date");
        if (aMsg) {
          setError(`${accLabel}: ${aMsg}`);
          return;
        }
      }
      const depositChanges = accountChanges.deposits || {};
      for (const indexKey of Object.keys(depositChanges)) {
        const idx = parseInt(indexKey, 10);
        if (isNaN(idx)) continue;
        const existing = account.deposits?.[idx];
        if (!existing?.id) continue;
        const change = depositChanges[indexKey] || {};
        if (Object.keys(change).length === 0) continue;
        const startDate = change.start_date !== undefined ? change.start_date : existing.start_date;
        if (startDate) {
          const dMsg = validateScheduleFutureYmd(normalizeToYmd(startDate), "Contribution start date");
          if (dMsg) {
            setError(`${accLabel}: ${dMsg}`);
            return;
          }
        }
      }
      const withdrawalChanges = accountChanges.withdrawals || {};
      for (const indexKey of Object.keys(withdrawalChanges)) {
        const idx = parseInt(indexKey, 10);
        if (isNaN(idx)) continue;
        const existing = account.withdrawals?.[idx];
        if (!existing?.id) continue;
        const change = withdrawalChanges[indexKey] || {};
        if (Object.keys(change).length === 0) continue;
        const startDate = change.start_date !== undefined ? change.start_date : existing.start_date;
        if (startDate) {
          const wMsg = validateScheduleFutureYmd(normalizeToYmd(startDate), "Withdrawal start date");
          if (wMsg) {
            setError(`${accLabel}: ${wMsg}`);
            return;
          }
        }
      }
    }

    setSavingAccounts(true);
    setError("");
    setSuccess("");

    try {
      const accountsPayload = {};

      const allAccountIds = new Set([
        ...Object.keys(accountsFormData),
        ...Object.keys(removedBeneficiaries),
        ...Object.keys(removedTrustedContacts),
      ]);

      allAccountIds.forEach((accountId) => {
        const account = household.accounts?.find((a) => a.id === accountId);
        if (!account) return;

        const accountChanges = accountsFormData[accountId] || {};
        const accountPayload = {};

        [
          "new_account_number", "custodian", "registration",
          "fee_billed_percentage", "account_value",
          "proceeds", "security_purchases", "distribution_income",
          "margin", "options", "fee_based_account", "check_writing",
        ].forEach((field) => {
          if (accountChanges[field] !== undefined) {
            const raw = accountChanges[field];
            if (["fee_billed_percentage", "account_value"].includes(field)) {
              accountPayload[field] = raw !== "" && raw !== null && raw !== undefined ? parseFloat(raw) : null;
            } else {
              accountPayload[field] = raw;
            }
          }
        });

        if (accountChanges.bankAccountsOps) {
          const op = buildClientBankAccountsOp(accountChanges.bankAccountsOps);
          if (op) accountPayload.bank_accounts = op;
        }

        if (accountChanges.owners_info) {
          const baseOwners = Array.isArray(account.owners_info) ? [...account.owners_info] : [];
          const changesMap = accountChanges.owners_info || {};
          const maxIdx = Math.max(
            baseOwners.length - 1,
            ...Object.keys(changesMap).map(Number).filter((n) => !isNaN(n)),
          );
          const newOwners = [];
          for (let idx = 0; idx <= maxIdx; idx++) {
            const existing = baseOwners[idx] || {};
            const ownerChanges = changesMap[idx] || {};
            const resolvedId = ownerChanges.id !== undefined ? ownerChanges.id : existing.id;
            if (!resolvedId) continue;
            newOwners.push({
              ...existing,
              id: resolvedId,
              type: ownerChanges.type !== undefined ? ownerChanges.type : (existing.type || "primary"),
            });
          }
          const ownerShare = newOwners.length >= 2 ? 50 : 100;
          accountPayload.owners_info = newOwners.map((o) => ({ ...o, share: ownerShare }));
        }

        if (accountChanges.transfer_info) {
          const mergedAddress = accountChanges.transfer_info.address
            ? { ...(account.transfer_info?.address || {}), ...accountChanges.transfer_info.address }
            : account.transfer_info?.address;
          if (mergedAddress?.phone) {
            mergedAddress.phone = String(mergedAddress.phone).replace(/\D/g, "");
          }
          accountPayload.transfer_info = {
            ...(account.transfer_info || {}),
            ...accountChanges.transfer_info,
            address: mergedAddress,
          };
        }

        if (accountChanges.risk_tolerance) {
          const baseRisk = account.risk_tolerance || {};
          const changes = accountChanges.risk_tolerance || {};
          accountPayload.risk_tolerance = { ...baseRisk, ...changes };

          if (changes.invest_objectives?.[0] !== undefined) {
            accountPayload.risk_tolerance.invest_objectives = changes.invest_objectives[0]
              ? [changes.invest_objectives[0]]
              : [];
          }
          if (changes.source_of_funds !== undefined) {
            accountPayload.risk_tolerance.source_of_funds = Array.isArray(changes.source_of_funds)
              ? changes.source_of_funds
              : changes.source_of_funds ? [changes.source_of_funds] : [];
          }
          if (changes.time_horizon) {
            accountPayload.risk_tolerance.time_horizon = {
              ...(baseRisk.time_horizon || {}),
              ...changes.time_horizon,
            };
          }
          if (changes.liquidity_requirements) {
            accountPayload.risk_tolerance.liquidity_requirements = {
              ...(baseRisk.liquidity_requirements || {}),
              ...changes.liquidity_requirements,
            };
            if (changes.liquidity_requirements.amount) {
              accountPayload.risk_tolerance.liquidity_requirements.amount = {
                ...(baseRisk.liquidity_requirements?.amount || {}),
                ...changes.liquidity_requirements.amount,
              };
            }
            if (changes.liquidity_requirements.time_horizon) {
              accountPayload.risk_tolerance.liquidity_requirements.time_horizon = {
                ...(baseRisk.liquidity_requirements?.time_horizon || {}),
                ...changes.liquidity_requirements.time_horizon,
              };
            }
          }
        }

        if (accountChanges.trust_info) {
          const baseTrust = account.trust_info || {};
          // Extract grantorIds from changes (if provided), else read from existing
          const grantorIdsChange = accountChanges.trust_info.grantorIds;
          const resolvedGrantorIds = Array.isArray(grantorIdsChange)
            ? grantorIdsChange.filter(Boolean)
            : getTrustGrantorIds(baseTrust);

          // Build patch without grantorIds (we handle that via buildTrustInfoUpdatePayload)
          const { grantorIds: _drop, ...trustPatch } = accountChanges.trust_info;

          // Merge trustees from form changes
          if (trustPatch.trustees) {
            const baseTrustees = Array.isArray(baseTrust.trustees)
              ? baseTrust.trustees.map((t) => trusteeEntryToObject(t))
              : [];
            const trusteeChanges = trustPatch.trustees;
            Object.keys(trusteeChanges).forEach((indexKey) => {
              const idx = parseInt(indexKey, 10);
              if (isNaN(idx)) return;
              while (baseTrustees.length <= idx) baseTrustees.push({});
              baseTrustees[idx] = {
                ...(baseTrustees[idx] || {}),
                ...trusteeChanges[indexKey],
              };
            });
            // finalizeTrusteesForPayload uses first grantorId for "same as grantor" skip
            trustPatch.trustees = finalizeTrusteesForPayload(
              baseTrustees,
              resolvedGrantorIds[0] || null,
            );
          }

          // Use buildTrustInfoUpdatePayload to produce final trust_info (writes grantors[], never grantor_or_settler)
          accountPayload.trust_info = buildTrustInfoUpdatePayload(baseTrust, {
            grantorIds: resolvedGrantorIds,
            patch: trustPatch,
          });

          const trustInfoPayload = accountPayload.trust_info || {};
          const isRevocable = isRevocableTrust(account?.type);

          if (isRevocable) {
            // Use first grantor for SSN derivation
            const primaryGrantorId = resolvedGrantorIds[0] || null;
            const grantorDigits = getMemberSsnDigitsById(primaryGrantorId);
            trustInfoPayload.ssn_or_tin =
              grantorDigits.length === 9 ? grantorDigits : null;
          } else if (trustInfoPayload.ssn_or_tin !== undefined) {
            const digits = String(trustInfoPayload.ssn_or_tin || "").replace(
              /\D/g,
              "",
            );
            trustInfoPayload.ssn_or_tin = digits.length > 0 ? digits : null;
          }
        }

        if (accountChanges.business_info) {
          const baseBiz = account.business_info || {};
          const bizChanges = accountChanges.business_info;
          accountPayload.business_info = { ...baseBiz, ...bizChanges };
          if (bizChanges.address) {
            accountPayload.business_info.address = {
              ...(baseBiz.address || {}),
              ...bizChanges.address,
            };
          }
          const resolvedAccountType =
            accountChanges.type !== undefined ? accountChanges.type : account.type;
          const taxOptions = getTaxClassificationOptions(resolvedAccountType);
          const taxValue = accountPayload.business_info.tax_classification;
          accountPayload.business_info.tax_classification = taxOptions.includes(taxValue)
            ? taxValue
            : null;
          if (bizChanges.is_operating !== undefined) {
            const v = bizChanges.is_operating;
            accountPayload.business_info.is_operating =
              v === true || v === "true" ? true : v === false || v === "false" ? false : null;
          }
          if (bizChanges.control_persons !== undefined) {
            accountPayload.business_info.control_persons = (
              Array.isArray(bizChanges.control_persons) ? bizChanges.control_persons : []
            )
              .filter((c) => c && c.id)
              .map((c) => ({ id: c.id }));
          }
        }

        if (accountChanges.inheritance_info) {
          const baseDec = account.inheritance_info?.decedent_info || {};
          const decChanges = accountChanges.inheritance_info?.decedent_info || {};
          const id = decChanges.id !== undefined ? decChanges.id : baseDec.id;
          const relationship =
            decChanges.relationship !== undefined
              ? decChanges.relationship || null
              : baseDec.relationship || null;
          accountPayload.inheritance_info = {
            decedent_info: id ? { id, relationship } : null,
          };
        }

        if (accountChanges.estate_info) {
          const baseEstate = account.estate_info || {};
          accountPayload.estate_info = { ...baseEstate, ...accountChanges.estate_info };
          // Merge executors array if changed
          if (Array.isArray(accountChanges.estate_info.executors)) {
            accountPayload.estate_info.executors =
              accountChanges.estate_info.executors.filter((r) => r?.id);
          }
        }

        if (accountChanges.other_info) {
          const baseOther = account.other_info || {};
          accountPayload.other_info = { ...baseOther, ...accountChanges.other_info };
          if (accountChanges.other_info.owners) {
            const baseOwners = Array.isArray(baseOther.owners) ? baseOther.owners : [];
            const ownerChanges = accountChanges.other_info.owners;
            const merged = [];
            if (ownerChanges._length !== undefined) {
              for (let i = 0; i < ownerChanges._length; i++) {
                const fullName = ownerChanges[i]?.full_name !== undefined
                  ? ownerChanges[i].full_name
                  : baseOwners[i]?.full_name;
                if (fullName) merged.push({ full_name: fullName });
              }
            } else {
              const maxIdx = Math.max(
                baseOwners.length - 1,
                ...Object.keys(ownerChanges).map(Number).filter((n) => !isNaN(n)),
              );
              for (let i = 0; i <= maxIdx; i++) {
                const fullName =
                  ownerChanges[i]?.full_name !== undefined
                    ? ownerChanges[i].full_name
                    : baseOwners[i]?.full_name;
                if (fullName) merged.push({ full_name: fullName });
              }
            }
            accountPayload.other_info.owners = merged;
          }
        }

        // Beneficiaries update/delete
        const beneficiaryOps = {};
        const beneficiaryDeletes = removedBeneficiaries[accountId] || [];
        const beneficiaryUpdates = {};
        const beneficiaryChanges = accountChanges.beneficiaries || {};
        Object.keys(beneficiaryChanges).forEach((indexKey) => {
          const idx = parseInt(indexKey, 10);
          const existing = account.beneficiaries?.[idx];
          if (!existing?.id || beneficiaryDeletes.includes(existing.id)) return;
          const change = beneficiaryChanges[indexKey] || {};
          const update = {};
          if (change.type !== undefined) update.type = change.type;
          if (change.share !== undefined) update.share = change.share !== "" ? parseFloat(change.share) : null;
          if (change.household_member_id !== undefined) update.household_member_id = change.household_member_id;
          if (change.relationship !== undefined) update.relationship = change.relationship || null;
          if (Object.keys(update).length) beneficiaryUpdates[existing.id] = update;
        });
        if (Object.keys(beneficiaryUpdates).length) beneficiaryOps.update = beneficiaryUpdates;
        if (beneficiaryDeletes.length) beneficiaryOps.delete = beneficiaryDeletes;
        if (Object.keys(beneficiaryOps).length) accountPayload.beneficiaries = beneficiaryOps;

        // Trusted contacts update/delete
        const trustedOps = {};
        const trustedDeletes = removedTrustedContacts[accountId] || [];
        const trustedUpdates = {};
        const trustedChanges = accountChanges.trusted_contacts || {};
        Object.keys(trustedChanges).forEach((indexKey) => {
          const idx = parseInt(indexKey, 10);
          const existing = account.trusted_contacts?.[idx];
          if (!existing?.id || trustedDeletes.includes(existing.id)) return;
          const change = trustedChanges[indexKey] || {};
          const update = {};
          if (change.household_member_id !== undefined) update.household_member_id = change.household_member_id;
          if (change.relationship !== undefined) update.relationship = change.relationship;
          if (Object.keys(update).length) trustedUpdates[existing.id] = update;
        });
        if (Object.keys(trustedUpdates).length) trustedOps.update = trustedUpdates;
        if (trustedDeletes.length) trustedOps.delete = trustedDeletes;
        if (Object.keys(trustedOps).length) accountPayload.trusted_contacts = trustedOps;

        // Deposits (contributions) update
        const depositChanges = accountChanges.deposits || {};
        const depositUpdates = {};
        Object.keys(depositChanges).forEach((indexKey) => {
          const idx = parseInt(indexKey, 10);
          if (isNaN(idx)) return;
          const existing = account.deposits?.[idx];
          if (!existing?.id) return;
          const change = depositChanges[indexKey] || {};
          const update = {};
          if (change.amount !== undefined) update.amount = change.amount !== "" ? parseFloat(change.amount) : null;
          if (change.frequency !== undefined) update.frequency = change.frequency;
          if (change.start_date !== undefined) update.start_date = change.start_date;
          if (change.method !== undefined) update.method = change.method;
          if (Object.keys(update).length) depositUpdates[existing.id] = update;
        });
        if (Object.keys(depositUpdates).length) {
          accountPayload.deposits = { ...(accountPayload.deposits || {}), update: depositUpdates };
        }

        // Withdrawals update
        const withdrawalChanges = accountChanges.withdrawals || {};
        const withdrawalUpdates = {};
        Object.keys(withdrawalChanges).forEach((indexKey) => {
          const idx = parseInt(indexKey, 10);
          if (isNaN(idx)) return;
          const existing = account.withdrawals?.[idx];
          if (!existing?.id) return;
          const change = withdrawalChanges[indexKey] || {};
          const update = {};
          if (change.amount !== undefined) update.amount = change.amount !== "" ? parseFloat(change.amount) : null;
          if (change.frequency !== undefined) update.frequency = change.frequency;
          if (change.start_date !== undefined) update.start_date = change.start_date;
          if (change.destination_type !== undefined) update.destination_type = change.destination_type;
          if (change.destination_identifier !== undefined) update.destination_identifier = change.destination_identifier || null;
          if (Object.keys(update).length) withdrawalUpdates[existing.id] = update;
        });
        if (Object.keys(withdrawalUpdates).length) {
          accountPayload.withdrawals = { ...(accountPayload.withdrawals || {}), update: withdrawalUpdates };
        }

        if (Object.keys(accountPayload).length > 0) {
          accountsPayload[accountId] = accountPayload;
        }
      });

      if (Object.keys(accountsPayload).length === 0) {
        setIsEditingAccounts(false);
        setAccountsFormData({});
        setSavingAccounts(false);
        return;
      }

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, { accounts: accountsPayload });
      if (result.success) {
        setSuccess(result.message || "Accounts updated successfully!");
        setIsEditingAccounts(false);
        setAccountsFormData({});
        setRemovedBeneficiaries({});
        setRemovedTrustedContacts({});
        setValidationErrors({});
        if (onDataUpdate && result.data) onDataUpdate(result.data);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(result.error || "Failed to update accounts.");
        if (result.sessionExpired) setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSavingAccounts(false);
    }
  };

  const handleCancelAccounts = () => {
    setIsEditingAccounts(false);
    setAccountsFormData({});
    setRemovedBeneficiaries({});
    setRemovedTrustedContacts({});
    setValidationErrors({});
  };

  const handleSaveBankAccounts = async () => {
    if (!actualHouseholdId || !clientToken) {
      setError("Missing required information.");
      return;
    }
    setSavingBankAccounts(true);
    setError("");
    setSuccess("");

    try {
      const accountsPayload = {};
      const orphanedEdits = [];

      Object.keys(bankAccountsFormData).forEach((bankId) => {
        const changes = bankAccountsFormData[bankId];
        if (!changes || Object.keys(changes).length === 0) return;

        const linkedAccounts = findHouseholdAccountsLinkedToBank(household, bankId);
        if (linkedAccounts.length === 0) {
          const bank = (household.bank_accounts || []).find((b) => b.id === bankId);
          orphanedEdits.push(bank?.bank_name || bankId);
          return;
        }

        // Route the update through the first linked account — the backend
        // resolves it against the bank id regardless of which account it rides.
        const linkedAccount = linkedAccounts[0];
        const existingPayload = accountsPayload[linkedAccount.id] || {};
        const existingOp = existingPayload.bank_accounts || {};
        const existingUpdates = Array.isArray(existingOp.update) ? existingOp.update : [];

        accountsPayload[linkedAccount.id] = {
          ...existingPayload,
          bank_accounts: {
            ...existingOp,
            update: [...existingUpdates, { id: bankId, ...changes }],
          },
        };
      });

      if (orphanedEdits.length > 0) {
        setError(
          `Could not save changes for ${orphanedEdits.length === 1 ? "bank account" : "bank accounts"}: ${orphanedEdits.join(
            ", ",
          )}. These bank accounts are not linked to any investment account — please link them first and try again.`,
        );
        setSavingBankAccounts(false);
        return;
      }

      if (Object.keys(accountsPayload).length === 0) {
        setIsEditingBankAccounts(false);
        setBankAccountsFormData({});
        setSavingBankAccounts(false);
        return;
      }

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, { accounts: accountsPayload });
      if (result.success) {
        setSuccess(result.message || "Bank accounts updated successfully!");
        setIsEditingBankAccounts(false);
        setBankAccountsFormData({});
        setValidationErrors({});
        if (onDataUpdate && result.data) onDataUpdate(result.data);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(result.error || "Failed to update bank accounts.");
        if (result.sessionExpired) setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSavingBankAccounts(false);
    }
  };

  const handleCancelBankAccounts = () => {
    setIsEditingBankAccounts(false);
    setBankAccountsFormData({});
    setValidationErrors({});
  };

  if (!householdData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto" />
          <p className="mt-4 text-xl text-gray-600">Loading your information</p>
        </div>
      </div>
    );
  }

  return (
    <ClientPortalLayout
      householdData={householdData}
      currentStep="review-submit"
      title="Review and edit your entire household record"
      subtitle="Please verify all information before submitting to your advisor."
      backLink={() => navigate(`/household/${actualHouseholdId}/pending-data`)}
      backLinkText="Back to Pending Data"
      demoVideoUrl={process.env.REACT_APP_DEMO_VIDEO_URL}
    >
      {/* Error / Success banners */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {householdData?.advisor_note &&
        householdData.advisor_note.trim() !== "" && (
          <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h8M8 14h6m-2 7H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v8l-4 4z"
                />
              </svg>
              <div>
                <p className="text-xs font-semibold text-emerald-800">
                  Message from your advisor
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {householdData.advisor_note}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={confirmClearAdvisorNote}
              disabled={clearingAdvisorNote}
              className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {clearingAdvisorNote ? "Deleting..." : "Delete"}
            </button>
          </div>
        )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tabs + content; submit panel stacks below on mobile, side column on lg */}
        <div className="flex-1 min-w-0">
          <TabBar
            tabs={[
              { key: "household", label: "Household Info" },
              { key: "members", label: "Household Members", count: memberCount },
              { key: "accounts", label: "Accounts", count: accountCount },
              { key: "bank_accounts", label: "Bank Accounts", count: bankAccountCount },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {activeTab === "household" && (
            <ReviewHouseholdInfoTab
              householdData={householdData}
              isEditing={isEditing}
              formData={householdFormData}
              onChange={handleHouseholdFieldChange}
              onEdit={() => setIsEditing(true)}
              onSave={handleSaveHousehold}
              onCancel={handleCancelHousehold}
              saving={saving}
              validationErrors={validationErrors}
              onFieldError={handleFieldError}
            />
          )}

          {activeTab === "members" && (
            <ReviewMembersTab
              householdData={householdData}
              isEditing={isEditingMembers}
              formData={membersFormData}
              onChange={handleMemberFieldChange}
              onEdit={() => setIsEditingMembers(true)}
              onSave={handleSaveMembers}
              onCancel={handleCancelMembers}
              saving={savingMembers}
              validationErrors={validationErrors}
              onFieldError={handleFieldError}
              onAddHouseholdMember={() => {
                addMemberForBeneficiaryFlowRef.current = false;
                setShowAddMemberModal(true);
              }}
            />
          )}

          {activeTab === "accounts" && (
            <ReviewAccountsTab
              householdData={householdData}
              isEditing={isEditingAccounts}
              formData={accountsFormData}
              onChange={handleAccountFieldChange}
              onEdit={() => setIsEditingAccounts(true)}
              onSave={handleSaveAccounts}
              onCancel={handleCancelAccounts}
              onAddBeneficiary={handleAddBeneficiaryForAccount}
              onAddTrustedContact={handleAddTrustedContactForAccount}
              onRemoveBeneficiary={handleRemoveBeneficiary}
              onRemoveTrustedContact={handleRemoveTrustedContact}
              removedBeneficiaries={removedBeneficiaries}
              removedTrustedContacts={removedTrustedContacts}
              onAddContribution={handleAddContribution}
              onAddWithdrawal={handleAddWithdrawal}
              onDeleteDeposit={handleDeleteDeposit}
              onDeleteWithdrawal={handleDeleteWithdrawal}
              onLinkBankAccount={handleLinkBankAccount}
              onRemoveBankAccount={handleRemoveBankAccount}
              saving={savingAccounts}
              validationErrors={validationErrors}
              onFieldError={handleFieldError}
            />
          )}

          {activeTab === "bank_accounts" && (
            <ReviewBankAccountsTab
              householdData={householdData}
              household_id={actualHouseholdId}
              clientToken={clientToken}
              isEditing={isEditingBankAccounts}
              formData={bankAccountsFormData}
              onChange={handleBankAccountFieldChange}
              onEdit={() => setIsEditingBankAccounts(true)}
              onSave={handleSaveBankAccounts}
              onCancel={handleCancelBankAccounts}
              saving={savingBankAccounts}
              validationErrors={validationErrors}
              onFieldError={handleFieldError}
              onDataUpdate={onDataUpdate}
              onAddBankAccount={() => setShowAddBankAccountModal(true)}
            />
          )}
        </div>

        {/* Submit panel: below content on mobile, right column on lg */}
        <div className="w-full lg:w-80 lg:flex-shrink-0 lg:self-start lg:sticky lg:top-0">
          <SubmitPanel
            household_id={actualHouseholdId}
            clientToken={clientToken}
            householdStatus={household.status}
            hasUnsavedEdits={hasUnsavedEdits}
            onSubmitSuccess={() => {
              setSuccess("Household submitted successfully for advisor review!");
              setTimeout(() => setSuccess(""), 5000);
            }}
          />
        </div>
      </div>

      {/* Select Member Type Modal (for beneficiary/trusted contact flow) */}
      <SelectMemberTypeModal
        isOpen={showSelectMemberTypeModal}
        onClose={() => {
          setShowSelectMemberTypeModal(false);
          setAccountForBeneficiary(null);
          setMemberTypeModalType(null);
        }}
        existingMembers={household.members || []}
        type={memberTypeModalType}
        accountOwnerIds={(() => {
          const acct = (household.accounts || []).find((a) => a.id === accountForBeneficiary);
          return (acct?.owners_info || []).map((o) => o.id);
        })()}
        excludeMemberIds={selectMemberModalExcludeMemberIds}
        onSelectExisting={handleSelectExistingMember}
        onAddNew={() => {
          setShowSelectMemberTypeModal(false);
          addMemberForBeneficiaryFlowRef.current = true;
          setShowAddMemberModal(true);
        }}
      />

      {/* Add New Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => {
          setShowAddMemberModal(false);
          addMemberForBeneficiaryFlowRef.current = false;
        }}
        household={household}
        onSuccess={(newMemberId) => {
          setShowAddMemberModal(false);
          if (addMemberForBeneficiaryFlowRef.current) {
            addMemberForBeneficiaryFlowRef.current = false;
            setNewlyCreatedMemberId(newMemberId);
            setShowBeneficiaryTrustedModal(true);
          } else {
            setSuccess("Household member added successfully!");
            setTimeout(() => setSuccess(""), 3000);
          }
        }}
        onHouseholdUpdate={onDataUpdate}
        clientToken={clientToken}
      />

      {/* Add Beneficiary / Trusted Contact Modal */}
      <AddMemberBeneficiaryTrustedModal
        isOpen={showBeneficiaryTrustedModal}
        onClose={() => {
          setShowBeneficiaryTrustedModal(false);
          setNewlyCreatedMemberId(null);
          setAccountForBeneficiary(null);
        }}
        household={household}
        newlyCreatedMemberId={newlyCreatedMemberId}
        preSelectedAccountId={accountForBeneficiary}
        onHouseholdUpdate={onDataUpdate}
        clientToken={clientToken}
      />

      {/* Add Contribution Modal */}
      <AddContributionModal
        isOpen={showAddContributionModal}
        onClose={() => {
          setShowAddContributionModal(false);
          setAccountForMoneyMovement(null);
        }}
        account={accountForMoneyMovement}
        household={household}
        onSuccess={async (depositPayload) => {
          const accountId = accountForMoneyMovement?.id;
          setShowAddContributionModal(false);
          setAccountForMoneyMovement(null);
          if (!accountId || !depositPayload) return;
          const result = await updateHouseholdAPI(actualHouseholdId, clientToken, {
            accounts: { [accountId]: { deposits: { create: [depositPayload] } } },
          });
          if (result.success) {
            setSuccess("Contribution added successfully!");
            if (onDataUpdate && result.data) onDataUpdate(result.data);
            setTimeout(() => setSuccess(""), 3000);
          } else {
            setError(result.error || "Failed to add contribution.");
          }
        }}
      />

      {/* Add Withdrawal Modal */}
      <AddWithdrawalModal
        isOpen={showAddWithdrawalModal}
        onClose={() => {
          setShowAddWithdrawalModal(false);
          setAccountForMoneyMovement(null);
        }}
        account={accountForMoneyMovement}
        household={household}
        onSuccess={async (withdrawalPayload) => {
          const accountId = accountForMoneyMovement?.id;
          setShowAddWithdrawalModal(false);
          setAccountForMoneyMovement(null);
          if (!accountId || !withdrawalPayload) return;
          const result = await updateHouseholdAPI(actualHouseholdId, clientToken, {
            accounts: { [accountId]: { withdrawals: { create: [withdrawalPayload] } } },
          });
          if (result.success) {
            setSuccess("Withdrawal added successfully!");
            if (onDataUpdate && result.data) onDataUpdate(result.data);
            setTimeout(() => setSuccess(""), 3000);
          } else {
            setError(result.error || "Failed to add withdrawal.");
          }
        }}
        clientToken={clientToken}
        onHouseholdUpdate={onDataUpdate}
      />
      {/* Select Bank Account Modal */}
      <SelectBankAccountModal
        isOpen={showSelectBankAccountModal}
        onClose={() => {
          setShowSelectBankAccountModal(false);
          setAccountForBankLinking(null);
        }}
        bankAccounts={household.bank_accounts || []}
        onSelectExisting={handleSelectExistingBankAccount}
        onAddNew={() => {
          setShowSelectBankAccountModal(false);
          setAccountForBankLinking(null);
        }}
      />

      {/* Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={showAddBankAccountModal}
        onClose={() => setShowAddBankAccountModal(false)}
        household={household}
        clientToken={clientToken}
        onSuccess={() => {
          setShowAddBankAccountModal(false);
          if (onDataUpdate) onDataUpdate();
        }}
      />
    </ClientPortalLayout>
  );
};

export default ReviewAndEditV2;
